import {useQuery} from "@tanstack/react-query";
import {API} from "api";
import {selectBackgrounds} from "libs/bg-utils";

// Outer index = set number (1-based in the API, 0-based here after sortValuesByKey).
// Entries may be undefined when the API returns a sparse set (findBgUrl guards on this).
export type BgUrls = (string[] | undefined)[];

/**
 * React Query key for the /tiles/get_tilesets cache entry. The API is
 * product-agnostic — one tile_sets array covers every product under a
 * channel/type — so the key is intentionally scoped to (channel, type)
 * only. Adding product to this key fragments the cache and causes a
 * redundant fetch on every product switch (#251).
 *
 * Exported + used by both useBgData and useBackgrounds so the two stay
 * in sync. Guard in __tests__/useBgData.test.ts asserts the shape.
 */
export const getBackgroundsQueryKey = (channel: string | undefined, type: string) =>
  ["get-backgrounds", channel, type] as const;

/** Read-only view of the get-backgrounds RQ cache. */
export function useBgData(
  channel: string | undefined,
  type: string | undefined,
  {enabled: enabledGate = true}: {enabled?: boolean} = {}
) {
  const currentType = type ?? "general";
  const {data, isPending, isSuccess, isError, error} = useQuery({
    queryKey: getBackgroundsQueryKey(channel, currentType),
    queryFn: () => API.fetchBackgrounds(channel, currentType),
    select: selectBackgrounds as (raw: unknown) => BgUrls,
    enabled: !!channel && !!currentType && enabledGate,
    refetchOnWindowFocus: false,
  });

  return {
    allBgUrls: data ?? [],
    isPending,
    isSuccess,
    isError,
    error,
  };
}
